import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { createSlug } from '../../util/urlHelpers';
import { parse, stringify } from '../../util/urlHelpers';

import { IconSpinner, ListingCard, Page, OutsideClickHandler } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import ComparePicker from '../../components/Compare/ComparePicker';
import CompareTable from '../../components/Compare/CompareTable';
import { loadData, SUPPORTED_DEVICE_TYPE_IDS } from './ComparePage.duck';

import css from './ComparePage.module.css';
import searchPageCss from '../SearchPage/SearchPage.module.css';
import mainPanelHeaderCss from '../SearchPage/MainPanelHeader/MainPanelHeader.module.css';

const formatDeviceTypeLabel = deviceType => {
  if (!deviceType) return '';
  return deviceType
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\w\S*/g, t => t.charAt(0).toUpperCase() + t.substr(1).toLowerCase());
};

const normalizeDeviceType = value => {
  if (!value) return '';
  if (Array.isArray(value)) {
    return value.map(v => createSlug(`${v}`));
  }
  return createSlug(`${value}`);
};

const DEVICE_TYPES = [
  {
    id: 'camera-de-securite',
    label: 'Caméra de sécurité',
    selectTitle: 'Sélectionner les caméras à comparer',
    seoTitle: 'Comparateur de caméras de sécurité',
    seoDescription:
      'Comparez les caméras de surveillance connectées : résolution, vision nocturne, détection de mouvement, compatibilité domotique et prix. Trouvez la caméra de sécurité idéale pour votre maison intelligente.',
    seoIntro:
      'Comparez les caméras de sécurité et caméras de surveillance connectées côte à côte pour trouver le modèle adapté à votre installation. Analysez en détail les caractéristiques techniques essentielles : résolution vidéo, champ de vision, détection de mouvement, vision nocturne, stockage et compatibilité avec votre écosystème domotique.',
    categoryCta: {
      label: 'Voir toutes nos caméras',
      href: '/products/camera-securite',
    },
    guideLink: {
      label: 'Voir le guide d\'achat',
      href: '/camera-securite',
    },
  },
  {
    id: 'thermostat',
    label: 'Thermostat',
    selectTitle: 'Sélectionner les thermostats à comparer',
    seoTitle: 'Comparateur de thermostats connectés',
    seoDescription:
      'Comparez les thermostats connectés et intelligents : programmation, compatibilité chaudière, économies d\'énergie et protocoles domotiques. Choisissez le thermostat adapté à votre chauffage.',
    seoIntro:
      'Comparez les thermostats connectés et intelligents pour optimiser le chauffage de votre logement. Analysez les fonctionnalités clés : programmation horaire, géolocalisation, compatibilité chaudière, protocoles de communication et potentiel d\'économies d\'énergie.',
    categoryCta: {
      label: 'Voir tous nos thermostats',
      href: '/products/thermostat-connecte',
    },
    guideLink: {
      label: 'Voir le guide d\'achat',
      href: '/thermostat-connecte',
    },
  },
  {
    id: 'prise-connectee',
    label: 'Prise connectée',
    selectTitle: 'Sélectionner les prises connectées à comparer',
    seoTitle: 'Comparateur de prises connectées',
    seoDescription:
      'Comparez les prises connectées et intelligentes : puissance maximale, suivi de consommation, programmation, compatibilité assistants vocaux. Trouvez la prise connectée idéale pour votre maison.',
    seoIntro:
      'Comparez les prises connectées côte à côte pour trouver le modèle adapté à vos besoins. Évaluez la puissance maximale supportée, le suivi de consommation énergétique, les options de programmation et la compatibilité avec vos assistants vocaux et systèmes domotiques.',
    categoryCta: {
      label: 'Voir toutes nos prises connectées',
      href: '/products/prise-connectee',
    },
    guideLink: {
      label: 'Voir le guide d\'achat',
      href: '/prise-connectee',
    },
  },
  {
    id: 'serrure-connectee',
    label: 'Serrure connectée',
    selectTitle: 'Sélectionner les serrures connectées à comparer',
    seoTitle: 'Comparateur de serrures connectées',
    seoDescription:
      'Comparez les serrures connectées et intelligentes : type de verrouillage, compatibilité de porte, protocoles de communication et contrôle à distance. Sécurisez votre entrée avec la serrure adaptée.',
    seoIntro:
      'Comparez les serrures connectées pour sécuriser votre domicile avec la solution la plus adaptée. Analysez le type de verrouillage, la compatibilité avec votre porte, les protocoles de communication, les options d\'accès à distance et l\'intégration à votre système domotique.',
    categoryCta: {
      label: 'Voir toutes nos serrures connectées',
      href: '/products/serrure-connectee',
    },
    guideLink: {
      label: 'Voir le guide d\'achat',
      href: '/serrure-connectee',
    },
  },
];

export const ComparePageComponent = props => {
  const config = useConfiguration();
  const history = useHistory();
  const location = useLocation();
  const {
    params,
    listings,
    compareListingsInProgress,
    compareListingsError,
    onLoadData,
  } = props;

  const deviceTypeParam = params?.device_type;
  const isLandingMode = !deviceTypeParam;
  const matchingDeviceType = DEVICE_TYPES.find(dt => dt.id === deviceTypeParam);
  const isUnknownDeviceType = !isLandingMode && !matchingDeviceType;
  const currentDeviceType = matchingDeviceType;
  const normalizedDeviceType = normalizeDeviceType(deviceTypeParam);

  useEffect(() => {
    if (isUnknownDeviceType) return;
    onLoadData({ device_type: deviceTypeParam }, config);
  }, [deviceTypeParam, onLoadData, config]);

  const [selection, setSelection] = useState({
    firstListingId: '',
    secondListingId: '',
  });

  useEffect(() => {
    setSelection({ firstListingId: '', secondListingId: '' });
  }, [deviceTypeParam]);

  useEffect(() => {
    if (!selection.firstListingId || !selection.secondListingId) return;
    if (selection.firstListingId === selection.secondListingId) {
      setSelection({ firstListingId: selection.firstListingId, secondListingId: '' });
    }
  }, [selection.firstListingId, selection.secondListingId]);

  useEffect(() => {
    const paramsFromUrl = parse(location.search);
    const compareParam = paramsFromUrl.compare;
    if (!compareParam) return;
    const [firstListingIdRaw, secondListingIdRaw] = `${compareParam}`.split(',');
    const firstListingId = firstListingIdRaw || '';
    const secondListingId = secondListingIdRaw || '';
    if (!firstListingId && !secondListingId) return;
    if (
      firstListingId !== selection.firstListingId ||
      secondListingId !== selection.secondListingId
    ) {
      setSelection({ firstListingId, secondListingId });
    }
  }, [location.search]);

  useEffect(() => {
    const currentParams = parse(location.search);
    const compareEntries = [selection.firstListingId, selection.secondListingId].filter(Boolean);
    const compareValue = compareEntries.length > 0 ? compareEntries.join(',') : null;
    const nextParams = compareValue ? { compare: compareValue } : {};
    const nextSearch = stringify(nextParams);
    const nextUrl = `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`;
    const currentUrl = `${location.pathname}${location.search || ''}`;
    if (nextUrl !== currentUrl) {
      history.replace(nextUrl);
    }
  }, [selection.firstListingId, selection.secondListingId, history, location.pathname, location.search]);

  const selectedListings = useMemo(() => {
    const firstListing = (listings || []).find(l => l?.id?.uuid === selection.firstListingId);
    const secondListing = (listings || []).find(l => l?.id?.uuid === selection.secondListingId);
    return { firstListing, secondListing };
  }, [listings, selection.firstListingId, selection.secondListingId]);

  const activeDeviceType = useMemo(() => {
    if (!isLandingMode) return normalizedDeviceType;
    const picked = selectedListings.firstListing || selectedListings.secondListing;
    if (!picked) return null;
    const dt = normalizeDeviceType(picked?.attributes?.publicData?.deviceType);
    return Array.isArray(dt) ? dt[0] : dt;
  }, [isLandingMode, normalizedDeviceType, selectedListings]);

  useEffect(() => {
    if (!isLandingMode || !activeDeviceType || !selection.secondListingId) return;
    const secondListing = selectedListings.secondListing;
    if (!secondListing) return;
    const secondDt = normalizeDeviceType(secondListing?.attributes?.publicData?.deviceType);
    const secondMatches = Array.isArray(secondDt)
      ? secondDt.includes(activeDeviceType)
      : secondDt === activeDeviceType;
    if (!secondMatches) {
      setSelection(prev => ({ ...prev, secondListingId: '' }));
    }
  }, [isLandingMode, activeDeviceType, selection.secondListingId, selectedListings.secondListing]);

  const activeDeviceTypeConfig = useMemo(() => {
    if (!isLandingMode || !activeDeviceType) return null;
    return DEVICE_TYPES.find(dt => dt.id === activeDeviceType) || null;
  }, [isLandingMode, activeDeviceType]);

  const compatibleListings = useMemo(() => {
    if (!isLandingMode) return listings || [];
    const supported = new Set(SUPPORTED_DEVICE_TYPE_IDS);
    return (listings || []).filter(listing => {
      const dt = normalizeDeviceType(listing?.attributes?.publicData?.deviceType);
      if (Array.isArray(dt)) return dt.some(v => supported.has(v));
      return supported.has(dt);
    });
  }, [listings, isLandingMode]);

  const filteredListings = useMemo(() => {
    const base = isLandingMode ? compatibleListings : (listings || []);
    if (!activeDeviceType) return base;
    return base.filter(listing => {
      const dt = normalizeDeviceType(listing?.attributes?.publicData?.deviceType);
      if (Array.isArray(dt)) return dt.includes(activeDeviceType);
      return dt === activeDeviceType;
    });
  }, [listings, compatibleListings, isLandingMode, activeDeviceType]);

  const deviceTypeLabel = isLandingMode
    ? (activeDeviceTypeConfig?.label || '')
    : (currentDeviceType?.label || formatDeviceTypeLabel(deviceTypeParam));
  const selectTitle = isLandingMode
    ? (activeDeviceTypeConfig?.selectTitle || 'Sélectionner les objets connectés à comparer')
    : (currentDeviceType?.selectTitle || `Sélectionner les ${deviceTypeLabel}`);

  const pageTitle = isLandingMode
    ? 'Comparateur d\'objets connectés pour la maison'
    : (currentDeviceType?.seoTitle ||
       `Comparateur ${formatDeviceTypeLabel(deviceTypeParam)}`);
  const pageDescription = isLandingMode
    ? 'Comparez les objets connectés pour la maison intelligente : caméras de sécurité, thermostats, prises et serrures connectées. Comparez les prix, caractéristiques techniques et compatibilités côte à côte pour faire le bon choix.'
    : (currentDeviceType?.seoDescription ||
       'Comparez deux produits similaires par prix, images et spécifications techniques.');

  const handleDeviceTypeChange = e => {
    const nextDeviceType = e.target.value;
    history.push(`/comparateur/${nextDeviceType}`);
  };

  const [isDeviceTypeOpen, setIsDeviceTypeOpen] = useState(false);

  const handleInternalNav = useCallback(
    (e, href) => {
      if (href && typeof href === 'string' && href.startsWith('/')) {
        e.preventDefault();
        history.push(href);
      }
    },
    [history]
  );

  const guideIcon = (
    <span className={searchPageCss.categoryCtaIcon} aria-hidden="true">
      <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" fill="none">
        <g fill="currentColor">
          <path d="M12.75 1a.75.75 0 000 1.5h.69l-1.97 1.97a.75.75 0 001.06 1.06l1.97-1.97v.69a.75.75 0 001.5 0v-2.5a.75.75 0 00-.75-.75h-2.5z"></path>
          <path
            fillRule="evenodd"
            d="M1.25 2C.56 2 0 2.56 0 3.25v8.5C0 12.44.56 13 1.25 13H5c.896 0 1.475.205 1.809.448.317.23.441.51.441.802a.751.751 0 101.5 0c0-.292.124-.572.441-.802.334-.243.913-.448 1.809-.448h3.75c.69 0 1.25-.56 1.25-1.25v-4.5a.75.75 0 00-1.5 0v4.25H11c-.878 0-1.64.158-2.25.467v-6.55c0-.788.376-1.42 1.12-1.722a.75.75 0 00-.561-1.39 3.27 3.27 0 00-1.31.941A3.13 3.13 0 007.773 3C7.106 2.354 6.154 2 5 2H1.25zm6 3.417c0-.553-.187-1.015-.522-1.34C6.394 3.753 5.846 3.5 5 3.5H1.5v8H5c.878 0 1.64.158 2.25.467v-6.55z"
            clipRule="evenodd"
          ></path>
        </g>
      </svg>
    </span>
  );

  const handleDeviceTypeSelect = deviceTypeId => {
    setIsDeviceTypeOpen(false);
    history.push(`/comparateur/${deviceTypeId}`);
  };

  if (isUnknownDeviceType) {
    return (
      <Page
        title="Comparateur introuvable"
        description="Ce type d’appareil n’est pas pris en charge."
        socialSharing={{
          title: 'Comparateur introuvable',
          description: 'Ce type d’appareil n’est pas pris en charge.',
        }}
        noIndex
      >
        <TopbarContainer rootClassName={css.topbar} currentSearchParams={{}} />
        <div className={css.layoutWrapperContainer}>
          <div className={css.layoutWrapperMain} role="main">
            <div className={css.searchResultContainer}>
              <div className={searchPageCss.mobileHeadingWrapper}>
                <h1 className={searchPageCss.mobileHeading}>Comparateur introuvable</h1>
                <p className={searchPageCss.mobileCategoryIntro}>
                  Ce type d’appareil n’est pas pris en charge par le comparateur.
                </p>
              </div>
              <header className={css.compareDesktopHeader}>
                <div className={mainPanelHeaderCss.categoryHeadingWrapper}>
                  <div className={mainPanelHeaderCss.categoryHeadingRow}>
                    <h1 className={mainPanelHeaderCss.categoryHeading}>Comparateur introuvable</h1>
                  </div>
                  <p className={mainPanelHeaderCss.categoryIntro}>
                    Ce type d’appareil n’est pas pris en charge par le comparateur.
                  </p>
                </div>
              </header>
              <div className={css.divider} />
              <p className={css.stateMessage}>Vérifiez le lien ou sélectionnez un autre type.</p>
            </div>
          </div>
        </div>
        <FooterContainer />
      </Page>
    );
  }

  return (
    <Page
      title={pageTitle}
      description={pageDescription}
      socialSharing={{
        title: pageTitle,
        description: pageDescription,
      }}
    >
      <TopbarContainer rootClassName={css.topbar} currentSearchParams={{}} />
      <div className={css.layoutWrapperContainer}>
        <div className={css.layoutWrapperMain} role="main">
          <div className={css.searchResultContainer}>
            <div className={searchPageCss.mobileHeadingWrapper}>
              <h1 className={searchPageCss.mobileHeading}>{pageTitle}</h1>
              {currentDeviceType?.guideLink || currentDeviceType?.categoryCta ? (
                <div className={css.compareMobileCtas}>
                  <div className={css.compareMobileCtaRow}>
                    {currentDeviceType?.guideLink ? (
                      <a
                        className={classNames(
                          searchPageCss.categoryGuideButton,
                          searchPageCss.categoryCtaFixedButton,
                          css.comparePageCta
                        )}
                        href={currentDeviceType.guideLink.href}
                        onClick={e => handleInternalNav(e, currentDeviceType.guideLink.href)}
                      >
                        {guideIcon}
                        {currentDeviceType.guideLink.label}
                      </a>
                    ) : null}
                    {currentDeviceType?.categoryCta ? (
                      <a
                        className={classNames(
                          searchPageCss.categoryCtaButton,
                          searchPageCss.categoryCtaFixedButton,
                          css.comparePageCta
                        )}
                        href={currentDeviceType.categoryCta.href}
                        onClick={e => handleInternalNav(e, currentDeviceType.categoryCta.href)}
                      >
                        {currentDeviceType.categoryCta.label}
                      </a>
                    ) : null}
                  </div>
                </div>
              ) : null}
              {isLandingMode ? (
                <p className={searchPageCss.mobileCategoryIntro}>
                  Comparez les objets connectés de votre maison intelligente en quelques clics. Sélectionnez un premier produit pour lancer la comparaison : les résultats s'affineront automatiquement par catégorie d'appareil.
                </p>
              ) : currentDeviceType?.seoIntro ? (
                <p className={searchPageCss.mobileCategoryIntro}>{currentDeviceType.seoIntro}</p>
              ) : null}
            </div>
            <header className={css.compareDesktopHeader}>
              <div className={mainPanelHeaderCss.categoryHeadingWrapper}>
                <div className={mainPanelHeaderCss.categoryHeadingRow}>
                  <h1 className={mainPanelHeaderCss.categoryHeading}>{pageTitle}</h1>
                  {currentDeviceType?.guideLink || currentDeviceType?.categoryCta ? (
                    <div className={css.compareDesktopCtas}>
                      {currentDeviceType?.guideLink ? (
                        <a
                          className={classNames(
                            searchPageCss.categoryGuideButton,
                            searchPageCss.categoryCtaFixedButton,
                            css.comparePageCta
                          )}
                          href={currentDeviceType.guideLink.href}
                          onClick={e => handleInternalNav(e, currentDeviceType.guideLink.href)}
                        >
                          {guideIcon}
                          {currentDeviceType.guideLink.label}
                        </a>
                      ) : null}
                      {currentDeviceType?.categoryCta ? (
                        <a
                          className={classNames(
                            searchPageCss.categoryCtaButton,
                            searchPageCss.categoryCtaFixedButton,
                            css.comparePageCta
                          )}
                          href={currentDeviceType.categoryCta.href}
                          onClick={e => handleInternalNav(e, currentDeviceType.categoryCta.href)}
                        >
                          {currentDeviceType.categoryCta.label}
                        </a>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                {isLandingMode ? (
                  <p className={mainPanelHeaderCss.categoryIntro}>
                    Comparez les objets connectés de votre maison intelligente en quelques clics. Sélectionnez un premier produit pour lancer la comparaison : les résultats s'affineront automatiquement par catégorie d'appareil.
                  </p>
                ) : currentDeviceType?.seoIntro ? (
                  <p className={mainPanelHeaderCss.categoryIntro}>{currentDeviceType.seoIntro}</p>
                ) : null}
              </div>
            </header>

            <div className={css.divider} />
            <h2 className={css.sectionHeading}>{selectTitle}</h2>
            <div className={css.sectionCard}>
              <div className={css.compareGrid}>
                <ComparePicker
                  className={css.compareDropdowns}
                  listings={isLandingMode ? compatibleListings : filteredListings}
                  secondListings={filteredListings}
                  firstListingId={selection.firstListingId}
                  secondListingId={selection.secondListingId}
                  onChange={setSelection}
                  labelPrefix={deviceTypeLabel}
                />
                <div className={css.selectedListings}>
                  {selectedListings.firstListing ? (
                    <ListingCard
                      className={css.compactCard}
                      listing={selectedListings.firstListing}
                      renderSizes="(max-width: 767px) 70vw, 220px"
                      linkTarget="_blank"
                      linkRel="noopener noreferrer"
                    />
                  ) : (
                    <div className={css.selectedListingsPlaceholder} />
                  )}
                  {selectedListings.secondListing ? (
                    <ListingCard
                      className={css.compactCard}
                      listing={selectedListings.secondListing}
                      renderSizes="(max-width: 767px) 70vw, 220px"
                      linkTarget="_blank"
                      linkRel="noopener noreferrer"
                    />
                  ) : (
                    <div className={css.selectedListingsPlaceholder} />
                  )}
                </div>
              </div>
            </div>

            {compareListingsInProgress ? (
              <p className={css.stateMessage}>
                <IconSpinner /> Chargement des produits...
              </p>
            ) : null}
            {compareListingsError ? (
              <p className={classNames(css.stateMessage, css.error)}>
                Une erreur est survenue lors du chargement des produits.
              </p>
            ) : null}
            {!compareListingsInProgress && !compareListingsError && filteredListings.length === 0 ? (
              <p className={css.stateMessage}>
                {isLandingMode
                  ? 'Aucun produit disponible pour la comparaison.'
                  : 'Aucun produit trouvé pour ce type d’appareil.'}
              </p>
            ) : null}

            <div className={css.divider} />
            {selectedListings.firstListing && selectedListings.secondListing ? (
              <div className={css.tableCard}>
                <CompareTable
                  firstListing={selectedListings.firstListing}
                  secondListing={selectedListings.secondListing}
                />
              </div>
            ) : (
              <p className={css.stateMessage}>
                Choisissez deux produits de la même catégorie pour afficher le comparatif détaillé.
              </p>
            )}
          </div>
        </div>
      </div>
      <FooterContainer />
    </Page>
  );
};

const mapStateToProps = state => {
  const { listingIds, compareListingsInProgress, compareListingsError } = state.ComparePage;
  const listings = getListingsById(state, listingIds);

  return {
    listings,
    compareListingsInProgress,
    compareListingsError,
  };
};

const mapDispatchToProps = dispatch => ({
  onLoadData: (params, config) => dispatch(loadData(params, null, config)),
});

const ComparePage = connect(
  mapStateToProps,
  mapDispatchToProps
)(ComparePageComponent);

export default ComparePage;
